import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { PartyPopper } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * ReferralActivationsWatcher
 *
 * Mounted once at the app shell. While the user is signed in and not
 * on the login/register page, it polls `/api/referrals/activations/unread`
 * every 60 seconds and fires a celebratory toast for each new activation:
 *
 *     🎉 Friend just joined via your link — you're 2/3 to 20% off Premium!
 *
 * The polling endpoint marks the row `delivered_toast=true` atomically
 * on read so we can't double-fire the same activation across tabs. The
 * component renders nothing.
 */
const POLL_INTERVAL_MS = 60_000;
const FIRST_POLL_DELAY_MS = 2_500; // don't hammer immediately after login

export default function ReferralActivationsWatcher() {
  const { user } = useAuth();
  const inFlight = useRef(false);

  useEffect(() => {
    if (!user) return; // logged out — nothing to poll

    let cancelled = false;

    const poll = async () => {
      if (inFlight.current) return;
      inFlight.current = true;
      try {
        const r = await api.get("/referrals/activations/unread");
        const list = r.data?.activations || [];
        if (cancelled) return;
        for (const n of list) {
          const remaining = Math.max(0, (n.required || 3) - (n.activation_number || 0));
          const tail = n.unlocked_coupon
            ? "you just unlocked 20% off Premium!"
            : remaining === 1
              ? "1 more and you unlock 20% off Premium!"
              : `you're ${n.activation_number}/${n.required} to 20% off Premium.`;
          toast.custom(
            () => (
              <div
                data-testid="referral-activation-toast"
                className="flex items-start gap-3 rounded-xl bg-emerald-950 text-white shadow-xl p-4 max-w-sm border border-emerald-500/40"
              >
                <div className="shrink-0 w-9 h-9 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-300">
                  <PartyPopper size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold text-[13px] leading-tight">
                    {n.referee_name} just joined via your link
                  </div>
                  <div className="text-xs text-emerald-200 mt-1 leading-snug">
                    {tail}
                  </div>
                  {/* Mini progress bar */}
                  <div className="mt-2.5 h-1.5 rounded-full bg-emerald-900 overflow-hidden">
                    <div
                      className="h-full bg-emerald-400 transition-all"
                      style={{ width: `${Math.min(100, ((n.activation_number || 0) / (n.required || 3)) * 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            ),
            { duration: 7000, id: `referral-activation-${n.id}` }
          );
        }
      } catch {
        // Polling errors are silent — no one wants a toast telling them
        // the toast poller failed.
      } finally {
        inFlight.current = false;
      }
    };

    const first = setTimeout(poll, FIRST_POLL_DELAY_MS);
    const iv = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(first);
      clearInterval(iv);
    };
  }, [user]);

  return null;
}
